// Your profile (how and what you fish and hunt) and tips from the next two days' forecast.
// The rules themselves are in shared/tip-rules.ts, shared with the server's evening push job.
import type { TextKey } from "../shared/strings.ts";
import { forecastUrl, makeTips, parseForecast, type Forecast, type TipProfile } from "../shared/tip-rules.ts";
import { $, $maybe, el, icon, toast } from "./dom.ts";
import { fmtNum, fmtWhen, huntStyleLabel, list, methodLabel, speciesLabel, t } from "./i18n.ts";
import { getPosition } from "./position.ts";
import { cancelButton, closeSheet, grow, openSheet, primaryButton } from "./sheet.ts";
import { HUNT_STYLES, METHODS, SPECIES } from "./species.ts";
import { catches, onRender } from "./state.ts";
import { lsJson, lsSet } from "./util.ts";
import { learnedPatterns } from "./insights.ts";
import { pushProfileChanged } from "./push.ts";
import { SHELTER_KMS, setShelterKm, shelterKm, type ShelterKm } from "./trip/shelter-setting.ts";
import { hasPlus } from "./billing.ts";

/** The profile as saved: the tip profile plus an optional home area for the forecast. */
export interface Profile extends TipProfile {
  home: { lat: number; lon: number } | null;
}
export let profile: Profile | null = lsJson<Profile | null>("profile", null);
const emptyProfile = (): Profile => ({ fishMethods: [], fishSpecies: [], huntStyles: [], huntSpecies: [], home: null });
export function saveProfile(p: Profile): void {
  profile = p;
  lsSet("profile", JSON.stringify(p));
  pushProfileChanged();
}
export const hasProfile = (): boolean =>
  !!profile && profile.fishMethods.length + profile.fishSpecies.length + profile.huntStyles.length + profile.huntSpecies.length > 0;

/* ---------- forecast ---------- */
let forecast: Forecast | null = null;
/** Where the tips are for: your home area, or your newest catch. */
export function tipsPlace(): { lat: number; lon: number; src: "home" | "catch" } | null {
  if (profile?.home) return { ...profile.home, src: "home" };
  const c = catches.filter((x) => x.loc).sort((a, b) => b.time - a.time)[0];
  return c?.loc ? { lat: c.loc.lat, lon: c.loc.lon, src: "catch" } : null;
}
async function loadForecast(force: boolean): Promise<void> {
  const at = tipsPlace();
  if (!at || !navigator.onLine) return;
  if (!forecast) forecast = lsJson<Forecast | null>("forecast2", null);
  const same = forecast && Math.abs(forecast.at.lat - at.lat) < 0.05 && Math.abs(forecast.at.lon - at.lon) < 0.05;
  if (!force && same && forecast && Date.now() - forecast.fetched < 60 * 6e4) return;
  const fc = parseForecast(await (await fetch(forecastUrl(at.lat, at.lon))).json(), at.lat, at.lon);
  if (!fc) return;
  forecast = fc;
  lsSet("forecast2", JSON.stringify(fc));
}
function currentTips() {
  if (!forecast || !profile) return [];
  const offset = forecast.offset;
  // the "matches your best catches" tip is ReelHunt Plus; the other weather tips are free
  return makeTips(forecast, { ...profile, learned: hasPlus() ? learnedPatterns() : [] }, {
    t, list, num: fmtNum,
    // forecast times are the place's wall-clock time stored as UTC; shift back to real time for the phone's clock
    when: (localMs) => fmtWhen(localMs - offset * 1000),
  });
}

/* ---------- card on the Catches tab ---------- */
function renderTips(): void {
  const box = $maybe("#tips");
  if (!box) return;
  box.hidden = false;
  if (!hasProfile()) {
    box.replaceChildren(el("div", { class: "tipcard intro" }, el("div", {},
      el("b", { text: t("tipsSetupTitle") }), el("p", { text: t("tipsSetupText") })),
      el("button", { class: "btn primary small", type: "button", onclick: openProfile }, t("tipsSetupBtn"))));
    return;
  }
  const place = tipsPlace();
  if (!place) {
    box.replaceChildren(el("div", { class: "tipcard intro" }, el("div", {}, el("b", { text: t("tipsTitle") }), el("p", { text: t("tipsNoPlace") })),
      el("button", { class: "btn small", type: "button", onclick: openProfile }, t("tipsSetPlace"))));
    return;
  }
  const tips = currentTips();
  const head = el("div", { class: "tipshead" }, el("b", { text: t("tipsTitle") }),
    el("button", { class: "linkbtn", type: "button", onclick: openProfile }, t("tipsEdit")));
  const body = !forecast ? [el("p", { class: "hint", text: navigator.onLine ? t("tipsLoading") : t("tipsOffline") })]
    : tips.length ? tips.map((x) => el("div", { class: "tip " + x.kind }, icon(x.icon), el("div", {}, el("b", { text: x.title }), el("p", { text: x.text }))))
    : [el("p", { class: "hint", text: t("tipsNone") })];
  const foot = forecast ? el("p", { class: "tipsfoot", text: t(place.src === "home" ? "tipsForHome" : "tipsForCatch") }) : null;
  box.replaceChildren(el("div", { class: "tipcard" }, head, ...body, foot));
}
async function refreshTips(force: boolean): Promise<void> {
  if (hasProfile()) {
    try { await loadForecast(force); } catch (e) { console.warn("Forecast failed", e); }
  }
  renderTips();
}

/* ---------- profile sheet ---------- */
function chipGroup(id: TextKey, items: readonly string[], chosen: string[], label: (v: string) => string): HTMLElement {
  return el("div", { class: "f" }, el("span", { class: "lbl", text: t(id) }),
    el("div", { class: "chips", id: "pf-" + id }, ...items.map((v) => el("button", { class: "chip", type: "button", "data-v": v, "aria-pressed": String(chosen.includes(v)),
      onclick: (e) => e.currentTarget.setAttribute("aria-pressed", String(e.currentTarget.getAttribute("aria-pressed") !== "true")) }, label(v)))));
}
const picked = (id: TextKey) => [...document.querySelectorAll<HTMLElement>(`#pf-${id} .chip[aria-pressed="true"]`)].map((b) => b.dataset.v ?? "");

export function openProfile(): void {
  const p = profile ? structuredClone(profile) : emptyProfile();
  const homeText = el("p", { class: "hint", id: "pf-hometext" });
  const showHome = () => { homeText.textContent = p.home ? t("tipsHomeSet", p.home.lat.toFixed(3), p.home.lon.toFixed(3)) : t("tipsHomeNone"); };
  showHome();
  openSheet(t("profileTitle"), [
    el("p", { class: "hint", text: t("profileIntro") }),
    chipGroup("pfFishHow", METHODS.fish, p.fishMethods, methodLabel),
    chipGroup("pfFishWhat", SPECIES.fish, p.fishSpecies, speciesLabel),
    chipGroup("pfHuntHow", HUNT_STYLES, p.huntStyles, huntStyleLabel),
    chipGroup("pfHuntWhat", SPECIES.game, p.huntSpecies, speciesLabel),
    el("div", { class: "f" }, el("span", { class: "lbl", text: t("tipsHome") }), homeText,
      el("div", { class: "btns" },
        el("button", { class: "btn small", type: "button", onclick: async (e) => {
          const btn = e.currentTarget;
          btn.disabled = true;
          try { const pos = await getPosition(); p.home = { lat: pos.lat, lon: pos.lon }; showHome(); }
          catch { toast(t("locationOff")); }
          btn.disabled = false;
        } }, icon("locate"), t("locUse")),
        p.home ? el("button", { class: "btn small", type: "button", onclick: (e) => { p.home = null; showHome(); e.currentTarget.remove(); } }, t("tipsHomeClear")) : null)),
    // how far "Sheltered from the wind" looks (trip/shelter-ui.ts)
    el("div", { class: "f" }, el("span", { class: "lbl", text: t("shelterPfLabel") }),
      el("div", { class: "chips", id: "pf-shelterkm" }, ...SHELTER_KMS.map((k) => el("button", { class: "chip", type: "button", "data-v": String(k), "aria-pressed": String(shelterKm() === k),
        onclick: (e) => { e.currentTarget.parentElement?.querySelectorAll(".chip").forEach((c) => c.setAttribute("aria-pressed", String(c === e.currentTarget))); } }, t("shelterKmOpt", k)))),
      el("p", { class: "hint", text: t("shelterPfHint") })),
  ], [grow(), cancelButton(), primaryButton(t("save"), () => {
    const km = Number(document.querySelector<HTMLElement>("#pf-shelterkm .chip[aria-pressed=\"true\"]")?.dataset.v);
    if ((SHELTER_KMS as readonly number[]).includes(km)) setShelterKm(km as ShelterKm);
    saveProfile({ fishMethods: picked("pfFishHow"), fishSpecies: picked("pfFishWhat"), huntStyles: picked("pfHuntHow"), huntSpecies: picked("pfHuntWhat"), home: p.home });
    closeSheet(); toast(t("profileSaved")); renderProfileSummary(); void refreshTips(true);
  })]);
}
/** One line per activity under More → Profile. */
function renderProfileSummary(): void {
  const box = $maybe("#profile-summary");
  if (!box) return;
  const p = profile;
  if (!p || !hasProfile()) { box.textContent = t("profileEmpty"); return; }
  const parts: string[] = [];
  if (p.fishMethods.length || p.fishSpecies.length) parts.push(t("profileFishLine", [...p.fishMethods.map(methodLabel), ...p.fishSpecies.map(speciesLabel)].join(", ")));
  if (p.huntStyles.length || p.huntSpecies.length) parts.push(t("profileHuntLine", [...p.huntStyles.map(huntStyleLabel), ...p.huntSpecies.map(speciesLabel)].join(", ")));
  box.replaceChildren(...parts.map((x) => el("span", { text: x })));
}
$("#profile-edit").addEventListener("click", openProfile);

onRender(() => { renderTips(); renderProfileSummary(); });
export function tipsStart(): void {
  void refreshTips(false);
  setInterval(() => { if (document.visibilityState === "visible") void refreshTips(false); }, 30 * 6e4);
}
addEventListener("online", () => void refreshTips(false));
